/**
 * The nine routes every one of the five collections answers.
 *
 * The server builds its search surface with one factory called five times
 * rather than writing the routes out; this is the same move on the other side of
 * the wire. Most of what the service answers is these nine repeated, and a
 * client that wrote them out once per noun would be five places to fix a paging
 * parameter.
 *
 * What is NOT here is anything a single noun owns. `referencing` belongs to
 * Company alone and `by-slug` to Auction House alone; each noun's module adds
 * its own beside these.
 *
 * @param {Object} config
 * @param {string} config.basePath   `/companies`, `/dealerships`, and so on.
 * @param {Function} config.sendRequest The client's one seam.
 * @param {Function} config.buildUrl    The client's one URL builder.
 */
export function collectionMethods({ basePath, sendRequest, buildUrl }) {
    return {
        /**
         * Create one. The server stamps `_id`, `status`, `createdAt` and
         * `updatedAt` itself and accepts none of them on a body, so the caller
         * describes the thing and nothing else.
         *
         * @returns {Promise<Object>} The created document.
         */
        async create(body) {
            return await sendRequest(buildUrl(basePath), 'POST', body);
        },

        /**
         * A page of documents carrying one status, sorted by name.
         *
         * Every parameter is optional and an absent one is not sent, which is
         * what lets the server apply its own default — `status: 'active'`, and
         * `pageSize: 50` from entities-core. A client repeating those defaults
         * would own a second copy of them.
         *
         * @returns {Promise<{ meta: { totalCount: number }, documents: Object[] }>}
         */
        async list({ status, page, pageSize } = {}) {
            return await sendRequest(buildUrl(basePath, { status, page, pageSize }), 'GET');
        },

        /** One document by its identifier. */
        async read(id) {
            return await sendRequest(buildUrl(`${basePath}/${encodeURIComponent(id)}`), 'GET');
        },

        /**
         * Edit one, and answer the whole updated document.
         *
         * The body is passed through as given, which is load-bearing: on this
         * server `null` clears an optional property and an absent key leaves it
         * alone. A form that never filled `website` must not read as "clear it",
         * so nothing here fills a missing key in with `null`.
         */
        async patch(id, body) {
            return await sendRequest(buildUrl(`${basePath}/${encodeURIComponent(id)}`), 'PATCH', body);
        },

        /**
         * Move one through its lifecycle. A retired one leaves only through
         * `restore`.
         *
         * @param {'active'|'suspended'|'retired'} status
         */
        async setStatus(id, status) {
            return await sendRequest(
                buildUrl(`${basePath}/${encodeURIComponent(id)}/status`),
                'PUT',
                { status }
            );
        },

        /**
         * Bring a retired one back to active.
         *
         * The only way out of `retired`: `setStatus` refuses to move a retired
         * document (`RESTORE_REQUIRED`), and this refuses one that is not
         * retired (`NOT_RETIRED`). It has nowhere to go but `active`, so it
         * sends no body.
         */
        async restore(id) {
            return await sendRequest(
                buildUrl(`${basePath}/${encodeURIComponent(id)}/restore`),
                'POST'
            );
        },

        /**
         * Search with FQL.
         *
         * The query crosses the wire as the caller typed it. The server converts
         * it against the collection's declared surface in strict posture, and
         * that conversion IS the validation — a field the surface does not carry
         * cannot become a query. A rejected query answers 400 with spanned
         * diagnostics, which arrive on the thrown error's `diagnostics`.
         *
         * An absent or empty query matches everything.
         *
         * `sort` names one field the surface publishes and `direction` is `asc`
         * or `desc`. The server orders the whole match before paging, so page
         * two continues page one. Absent, it orders by name.
         *
         * @returns {Promise<{ meta: { totalCount: number }, documents: Object[] }>}
         */
        async search(q, { page, pageSize, sort, direction } = {}) {
            return await sendRequest(
                buildUrl(`${basePath}/search`, { q, page, pageSize, sort, direction }),
                'GET'
            );
        },

        /**
         * The values a field holds, for the dropdown a caller sees while typing.
         *
         * A field that offers no values answers an empty list rather than a
         * refusal — the surface says which those are, under `suggestions`.
         *
         * @returns {Promise<{ values: string[] }>}
         */
        async suggest(field, prefix, limit) {
            return await sendRequest(
                buildUrl(`${basePath}/suggest`, { field, prefix, limit }),
                'GET'
            );
        },

        /**
         * Which fields a query may name, what each one holds, and whether it has
         * values to offer.
         *
         * A search bar cannot draw its sidebar without this. It is read from the
         * server rather than restated here on purpose: the declaration is built
         * from the same schema the writes validate against, and a copy in the
         * client would be a second owner of one fact.
         *
         * @returns {Promise<{ fields: Object<string, { type: string, values?: Array, match?: string, suggestions?: string }> }>}
         */
        async searchableSurface() {
            return await sendRequest(buildUrl(`${basePath}/searchable-surface`), 'GET');
        }
    };
}

export default collectionMethods;
